import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy, Trash2, Check, Clock, Ban, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const ROLES = [
  ["OWNER", "Owner", "Full access, including settings and billing"],
  ["STAFF", "Staff", "Create and edit invoices, parties and payments"],
  ["VIEWER", "CA / Viewer", "Read-only access to everything"],
];

const ROLE_LABEL = Object.fromEntries(ROLES.map(([v, l]) => [v, l]));

const STATUS_TONE = {
  PENDING: "bg-amber-50 text-amber-700",
  ACCEPTED: "bg-emerald-50 text-emerald-700",
  REVOKED: "bg-slate-100 text-slate-500",
  EXPIRED: "bg-slate-100 text-slate-500",
};

export default function Team() {
  const { user, role } = useAuth();
  const isOwner = role === "OWNER";
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [usage, setUsage] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "STAFF" });
  const [lastInvite, setLastInvite] = useState(null);
  const [mail, setMail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/members").then(({ data }) => setMembers(data));
    api.get("/invitations").then(({ data }) => setInvites(data)).catch(() => setInvites([]));
    api.get("/usage").then(({ data }) => setUsage(data.limits.users));
    api.get("/mail-status").then(({ data }) => setMail(data)).catch(() => setMail(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invitations", form);
      setLastInvite(data);
      toast.success(data.email_delivered
        ? `Invitation emailed to ${data.email}`
        : `Invitation created for ${data.email} — send them the link`);
      setForm({ email: "", name: "", role: "STAFF" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Invite link copied — send it to them");
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Revoke the invitation for ${inv.email}?`)) return;
    try {
      await api.delete(`/invitations/${inv.id}`);
      toast.success("Invitation revoked");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changeRole = async (m, newRole) => {
    try {
      await api.patch(`/members/${m.membership_id}`, { role: newRole });
      toast.success(`${m.name} is now ${ROLE_LABEL[newRole]}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from this organisation?`)) return;
    try {
      await api.delete(`/members/${m.membership_id}`);
      toast.success(`${m.name} removed`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const pending = invites.filter((i) => i.status === "PENDING");
  const atCapacity = usage?.limit != null && usage.used >= usage.limit;

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Team</h1>
          <p className="mt-1 text-sm text-slate-500">
            {members.length} member{members.length === 1 ? "" : "s"}
            {pending.length ? ` · ${pending.length} pending invitation${pending.length === 1 ? "" : "s"}` : ""}
            {usage?.limit != null ? ` · plan allows ${usage.limit}` : ""}
          </p>
        </div>
        {isOwner && (
          <Button
            onClick={() => { setLastInvite(null); setOpen(true); }}
            disabled={atCapacity}
            data-testid="invite-btn"
            className="gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700"
          >
            <UserPlus size={16} /> Invite
          </Button>
        )}
      </div>

      {atCapacity && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800"
          data-testid="team-at-capacity">
          You are using {usage.used} of {usage.limit} seats on your plan. Upgrade in{" "}
          <a href="/billing" className="font-semibold underline">Billing</a> to invite more people.
        </div>
      )}

      <TableWrap>
        <table className="w-full text-sm" data-testid="members-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Name</th>
              <th className="px-3 py-2.5 text-left font-semibold">Email</th>
              <th className="px-3 py-2.5 text-left font-semibold">Role</th>
              <th className="px-4 py-2.5 text-right font-semibold" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {members.map((m) => (
              <tr key={m.membership_id} className="hover:bg-slate-50 transition-colors duration-150"
                data-testid={`member-row-${m.id}`}>
                <td className="px-4 py-2.5 font-medium">
                  {m.name}
                  {m.id === user?.id && (
                    <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] uppercase text-blue-700">You</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-slate-600">{m.email}</td>
                <td className="px-3 py-2.5">
                  {isOwner && m.id !== user?.id ? (
                    <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid={`member-role-${m.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-xs">{ROLE_LABEL[m.role]}</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {isOwner && m.id !== user?.id && (
                    <button onClick={() => remove(m)} data-testid={`member-remove-${m.id}`}
                      className="text-slate-400 transition-colors duration-150 hover:text-rose-600">
                      <Trash2 size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>

      <CardList empty="No members" testid="members-cards">
        {members.map((m) => (
          <DataCard
            key={m.membership_id}
            testid={`member-card-${m.id}`}
            title={m.name}
            subtitle={m.email}
            right={<span className="text-[11px] font-medium text-slate-600">{ROLE_LABEL[m.role]}</span>}
            actions={
              isOwner && m.id !== user?.id ? (
                <button className="text-xs font-medium text-rose-600"
                  data-testid={`member-remove-m-${m.id}`} onClick={() => remove(m)}>Remove</button>
              ) : null
            }
          />
        ))}
      </CardList>

      {!!invites.length && (
        <div className="rounded-md border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">Invitations</div>
          <div className="divide-y divide-slate-100" data-testid="invitations-list">
            {invites.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">{i.email}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${STATUS_TONE[i.status]}`}>
                      {i.status}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {ROLE_LABEL[i.role]} · invited by {i.invited_by_name} · {fmtDate(i.created_at)}
                    {i.status === "PENDING" ? ` · expires ${fmtDate(i.expires_at)}` : ""}
                  </div>
                </div>
                {isOwner && i.status === "PENDING" && (
                  <button onClick={() => revoke(i)} data-testid={`invite-revoke-${i.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-rose-600">
                    <Ban size={13} /> Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite someone to {""}this organisation</DialogTitle>
            <DialogDescription>
              {mail?.configured
                ? `They will get an email from ${mail.from_address} with a link to join. The link is the credential — it expires in 14 days and works once.`
                : "Email is not configured on this deployment, so you will get a link to send them yourself. The link is the credential — treat it like a password."}
            </DialogDescription>
          </DialogHeader>

          {lastInvite ? (
            <div className="space-y-3" data-testid="invite-link-panel">
              {lastInvite.email_delivered ? (
                <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                  <Check size={14} className="mt-0.5 shrink-0" />
                  Emailed to <strong>{lastInvite.email}</strong>. The link below is the same one,
                  in case it does not arrive.
                </div>
              ) : (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    Invitation created, but the email could not be sent
                    {lastInvite.email_reason === "not_configured"
                      ? " — email is not configured on this deployment."
                      : ` (${lastInvite.email_reason}).`}{" "}
                    Send them this link yourself.
                  </span>
                </div>
              )}
              <div className="flex gap-2">
                <Input readOnly value={lastInvite.invite_url} className="font-mono text-[11px]"
                  data-testid="invite-link" />
                <Button variant="outline" onClick={() => copy(lastInvite.invite_url)}
                  className="shrink-0 gap-1.5">
                  <Copy size={14} /> Copy
                </Button>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <Clock size={12} /> Expires in 14 days, and works once.
              </div>
            </div>
          ) : (
            <div className="grid gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Email</Label>
                <Input data-testid="invite-email" type="email" value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="colleague@example.com" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Name (optional)</Label>
                <Input data-testid="invite-name" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(([v, l, d]) => (
                      <SelectItem key={v} value={v}>
                        <span className="font-medium">{l}</span>
                        <span className="ml-1.5 text-[11px] text-slate-500">{d}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {lastInvite ? "Done" : "Cancel"}
            </Button>
            {!lastInvite && (
              <Button onClick={invite} disabled={busy || !form.email}
                data-testid="invite-save" className="bg-blue-600 hover:bg-blue-700">
                Create invitation
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
